import { ref, query, orderByChild, orderByKey, startAt, endAt, get } from 'firebase/database';
import { format } from 'date-fns';
import { db } from '../firebase';
import { isBookingActive } from './bookingAvailabilityUtil';
import { getGridRowsForDate, getTimeSlotsForCourt, isCourtAvailable, isSlotPassed } from './bookingUtils';
import { hasActivePromo } from './promoUtils';
import { buildAvailabilitySnapshot } from './availabilitySnapshotUtil';
import { AVAILABILITY_IMAGE_MAX_RANGE_DAYS } from '../constants/bookingConst';

// Data for availability images covering a RANGE of dates (Grid View ->
// Generate images -> date range). The single selected date needs no reads —
// the admin grid already holds it — but any other date has to be fetched,
// so this reads the whole range in two bounded queries rather than one
// listener per date.

/**
 * Parses 'yyyy-MM-dd' as a local calendar date. By field, not
 * new Date(str), which reads a bare date as UTC and can land a day early.
 * @param {string} dateStr
 * @returns {Date}
 */
export const parseDateKey = (dateStr) => {
  const [y, m, d] = String(dateStr || '').split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

/**
 * 'yyyy-MM-dd' for a Date's local calendar day.
 * @param {Date} date
 * @returns {string}
 */
export const toDateKey = (date) => format(date, 'yyyy-MM-dd');

/**
 * Every date from start to end inclusive, as 'yyyy-MM-dd'. Capped at
 * AVAILABILITY_IMAGE_MAX_RANGE_DAYS so a bad input can't run away.
 * @param {string} startStr
 * @param {string} endStr
 * @returns {string[]} empty when end is before start
 */
export const listDatesInRange = (startStr, endStr) => {
  const dates = [];
  const cursor = parseDateKey(startStr);
  const end = parseDateKey(endStr);
  while (cursor <= end && dates.length < AVAILABILITY_IMAGE_MAX_RANGE_DAYS) {
    dates.push(toDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

/**
 * Adds whole days to a 'yyyy-MM-dd' date.
 * @param {string} dateStr
 * @param {number} days
 * @returns {string}
 */
export const addDaysToKey = (dateStr, days) => {
  const date = parseDateKey(dateStr);
  date.setDate(date.getDate() + days);
  return toDateKey(date);
};

/**
 * Keeps a picked range valid: the end never before the start, and never more
 * than AVAILABILITY_IMAGE_MAX_RANGE_DAYS long. Whichever end the admin just
 * moved wins; the other follows it.
 * @param {{start: string, end: string}} range - 'yyyy-MM-dd' dates
 * @param {'start'|'end'} changed - which end was just picked
 * @returns {{start: string, end: string}}
 */
export const clampAvailabilityRange = ({ start, end }, changed) => {
  const span = AVAILABILITY_IMAGE_MAX_RANGE_DAYS - 1;
  if (changed === 'start') {
    if (end < start) return { start, end: start };
    if (end > addDaysToKey(start, span)) return { start, end: addDaysToKey(start, span) };
    return { start, end };
  }
  if (end < start) return { start: end, end };
  if (start < addDaysToKey(end, -span)) return { start: addDaysToKey(end, -span), end };
  return { start, end };
};

/**
 * Reads every active booking and every slot override from start to the day
 * AFTER end — that last day feeds the after-midnight rows of the end date.
 *
 * Bookings are reduced to which courts are taken in which hour: on an image
 * every kind of reservation (pending, partial, confirmed) reads Booked, so
 * nothing else about them is kept.
 *
 * @param {string} startStr - 'yyyy-MM-dd'
 * @param {string} endStr - 'yyyy-MM-dd', on or after start
 * @returns {Promise<{taken: Object<string, Object<string, Set<number>>>, overrides: Object<string, object>}>}
 *   taken[date][timeSlot] = Set of court numbers; overrides[date] = slotOverrides/{date}
 * @throws {Error} when the database can't be read
 */
export const fetchAvailabilityRangeData = async (startStr, endStr) => {
  if (!db) throw new Error('The database is not available. Please refresh and try again.');
  const lastStr = addDaysToKey(endStr, 1);
  try {
    const [bookingsSnap, overridesSnap] = await Promise.all([
      get(query(ref(db, 'bookings'), orderByChild('date'), startAt(startStr), endAt(lastStr))),
      get(query(ref(db, 'slotOverrides'), orderByKey(), startAt(startStr), endAt(lastStr))),
    ]);

    const taken = {};
    // Statement bodies: a truthy return from DataSnapshot.forEach stops it.
    bookingsSnap.forEach((child) => {
      const data = child.val();
      if (!isBookingActive(data) || !data?.date || !data.timeSlots) return;
      Object.entries(data.timeSlots).forEach(([timeSlot, courts]) => {
        const courtList = Array.isArray(courts) ? courts : Object.keys(courts || {});
        if (!taken[data.date]) taken[data.date] = {};
        if (!taken[data.date][timeSlot]) taken[data.date][timeSlot] = new Set();
        courtList.forEach((court) => { taken[data.date][timeSlot].add(Number(court)); });
      });
    });

    const overrides = {};
    overridesSnap.forEach((child) => { overrides[child.key] = child.val() || {}; });
    return { taken, overrides };
  } catch (error) {
    throw new Error(`Could not load availability for those dates: ${error?.message || 'unknown error'}`);
  }
};

/**
 * A resolveCell for buildAvailabilitySnapshot that works on ANY date in the
 * fetched range, applying the admin grid's own rules (see
 * AdminTimeSlotSelection's resolveImageCell): a booking reads Booked, then
 * the admin's override, then Passed by the clock, else Open. A court that is
 * switched off, or doesn't run that hour that day, is locked.
 *
 * @param {object} params
 * @param {object} params.config
 * @param {{taken: object, overrides: object}} params.data - fetchAvailabilityRangeData() output
 * @param {string|null} params.sportId - for promo lookups (multi-sport clubs)
 * @param {Date} [params.now] - the clock, for which hours have passed
 * @returns {(rowDate: string, timeSlot: string, court: number) => {status: string, locked: boolean, promo: boolean, passed: boolean}}
 */
export const buildRangeCellResolver = ({ config, data, sportId = null, now = new Date() }) => {
  const todayStr = toDateKey(now);
  const currentHour = now.getHours();
  // getTimeSlotsForCourt walks the slot sets; cached per date+court since the
  // image asks it once per hour.
  const courtHours = new Map();
  const runsHour = (rowDate, court, timeSlot) => {
    const key = `${rowDate}|${court}`;
    if (!courtHours.has(key)) courtHours.set(key, new Set(getTimeSlotsForCourt(config, rowDate, court)));
    return courtHours.get(key).has(timeSlot);
  };

  return (rowDate, timeSlot, court) => {
    const passed = Boolean(rowDate) && (
      rowDate < todayStr || (rowDate === todayStr && isSlotPassed(true, timeSlot, currentHour))
    );
    const booked = data?.taken?.[rowDate]?.[timeSlot]?.has(Number(court));
    const override = data?.overrides?.[rowDate]?.[timeSlot]?.[court];
    let status = 'open';
    if (booked) status = 'booked';
    else if (override) status = override;
    else if (passed) status = 'passed';
    return {
      status,
      locked: !isCourtAvailable(config, court) || !runsHour(rowDate, court, timeSlot),
      promo: hasActivePromo(config, { court, timeSlot, sportId, dateStr: rowDate }),
      passed,
    };
  };
};

/**
 * One availability snapshot per date in the range, built from a single
 * fetch, exactly as the grid's own "Generate images" builds the viewed day's.
 *
 * @param {object} params
 * @param {object} params.config
 * @param {string} params.startStr - 'yyyy-MM-dd'
 * @param {string} params.endStr - 'yyyy-MM-dd'
 * @param {number[]} params.courts - the court numbers on screen
 * @param {string|null} [params.sportId]
 * @param {string} [params.sportName]
 * @param {string} [params.siteHost]
 * @returns {Promise<object[]>} buildAvailabilitySnapshot() outputs, in date order
 * @throws {Error} when the dates can't be read
 */
export const loadAvailabilitySnapshotsForRange = async ({
  config, startStr, endStr, courts, sportId = null, sportName = '', siteHost = '',
}) => {
  const data = await fetchAvailabilityRangeData(startStr, endStr);
  const resolveCell = buildRangeCellResolver({ config, data, sportId });
  return listDatesInRange(startStr, endStr).map(dateStr => buildAvailabilitySnapshot({
    config,
    dateStr,
    rows: getGridRowsForDate(config, dateStr, courts),
    courts,
    resolveCell,
    sportName,
    siteHost,
  }));
};
